import { Fragment, useEffect, useRef, useState } from "react";

import { useGesture, useMove } from "@use-gesture/react";

// Hooks
import iconCuter from "../../assets/images/cut.svg";
import iconSeekbarExpand from "../../assets/images/seekbar-expand.svg";
import iconLiveSeekbar from "../../assets/images/seekbar-live.svg";
import iconPlaySeekbar from "../../assets/images/seekbar-play.svg";
import transitionIcon from "../../assets/images/transition.svg";
import iconVolume from "../../assets/images/volume.svg";
// import { SocketContext } from "../../contexts/socketContext";
import useDidMountEffect from "../../hooks/useDidMountEffect";

import * as FnSeekbar from "./__fn.seekbar";
// SVGs
import VolumeController from "./volume-controller";

const DELETE_OFFSET = 14;
const CUT_RANGE_SAFE_MARGIN = 4;

const PlayerControllers = () => {
  // const { socket } = useContext(SocketContext) || {};

  const [cutMode, setCutMode] = useState(false);
  const [showVolumeController, setShowVolumeController] = useState(false);
  const [volume, setVolume] = useState(50);

  const [seekbarData, setSeekbarData] = useState({
    seekbar_screens_list: [],
    skip_ranges: [],
  });
  const [seekbarState, setSeekbarState] = useState({
    value: 0,
    time: "00:00:00",
    offset: 0,
    max: 400, // ToDo: this should be read from server
    fakeMax: 400,
    left: 0,
    width: 0,
  });
  const [cutMarkerState, setCutMarkerState] = useState({
    offsetX: "0%",
    tooltip: "00:00:00",
    time: 0,
    mode: "start",
  });
  const [deleteTooltipState, setDeleteTooltipState] = useState({
    visible: false,
    offsetX: 0,
    screenIndex: -1,
  });
  const [cut, setCut] = useState([]);
  const [cuts, setCuts] = useState([]);
  const [isCursorInsideAnotherArea, setIsCursorInsideAnotherArea] = useState(false);
  const [screenWidths, setScreenWidths] = useState([]);

  const seekbarRef = useRef();
  const isDraggingRef = useRef(false);

  const resetDeleteTooltipState = () => {
    setDeleteTooltipState({
      offsetX: 0,
      visible: false,
      time: 0,
    });
  };

  const bindDrag = useGesture({
    onDragStart: () => {
      isDraggingRef.current = true;
    },
    onDragEnd: () => {
      isDraggingRef.current = false;

      // delete mode
      if (deleteTooltipState.visible) {
        const clone = [...cuts];

        let i = 0;
        for (; i < clone.length; i++) {
          const x = clone[i];
          if (x[0] === deleteTooltipState.time || x[1] === deleteTooltipState.time) break;
        }

        // delete from list
        clone.splice(i, 1);

        // update client
        setCuts(clone);

        // in order to hide delete tooltip
        resetDeleteTooltipState();

        // update server
        // FnSeekbar.updateSkipRange(clone);
      }
    },
    onDrag: ({ event, xy /* initial */ }) => {
      const isStart = /start/.test(event.target.className);
      const initialTime = +event.target.dataset.time;

      const offsetX = xy[0] - seekbarState.left;
      let time = FnSeekbar.pixelToSecond(offsetX, seekbarState.max, seekbarState.width);
      // const screenIndex = 0; // ?

      const clone = [...cuts];
      if (isStart) {
        const item = clone.find((x) => x[0] === initialTime);
        const endAt = item[1];

        // prevent cross end point
        time = Math.min(endAt, time);

        // prevent cross screen range
        for (let i = 0; i < seekbarData.seekbar_screens_list.length; i++) {
          const screen = seekbarData.seekbar_screens_list[i];

          if (endAt > screen.timeRange[0] && endAt < screen.timeRange[1]) {
            time = Math.max(time, screen.timeRange[0]);
            break;
          }
        }

        // prevent cross other ranges
        let sortedClone = [...cuts];
        let i = 1;

        sortedClone = sortedClone.sort(([a, b], [c, d]) => a - c || d - b);

        if (endAt < sortedClone[1][0]) {
          /* empty */
        } else {
          for (; i < sortedClone.length - 1; i++) if (endAt > sortedClone[i - 1][1] && endAt < sortedClone[i + 1][0]) break;

          time = Math.max(time, sortedClone[i - 1][1] + 2);
        }

        // delete mode
        if (endAt - time < DELETE_OFFSET) {
          setDeleteTooltipState({
            offsetX,
            visible: true,
            time,
          });
        } else {
          resetDeleteTooltipState();
        }

        item[0] = time;
      } else {
        const item = clone.find((x) => x[1] === initialTime);
        if (item) {
          const [startAt /* endAt */] = item;

          // prevent cross start point
          time = Math.max(startAt, time);

          // prevent cross screen range
          for (let i = 0; i < seekbarData.seekbar_screens_list.length; i++) {
            const screen = seekbarData.seekbar_screens_list[i];

            if (startAt > screen.timeRange[0] && startAt < screen.timeRange[1]) {
              time = Math.min(time, screen.timeRange[1]);
              break;
            }
          }

          // prevent cross other ranges
          let sortedClone = [...cuts];
          let i = 0;
          sortedClone = sortedClone.sort(([a, b], [c, d]) => a - c || d - b);
          for (; i < sortedClone.length - 1; i++) {
            const [s /* e */] = sortedClone[i + 1];
            if (startAt < s) break;
          }

          if (i < sortedClone.length - 1) time = Math.min(time, sortedClone[i + 1][0] - 2);

          // delete mode
          if (time - startAt < DELETE_OFFSET) {
            setDeleteTooltipState({
              offsetX,
              visible: true,
              time,
            });
          } else {
            resetDeleteTooltipState();
          }

          item[1] = time;
        }
      }

      setCuts(clone);
    },
  });

  const bindMove = useMove(({ xy /* dragging */ }) => {
    if (isDraggingRef.current) return;

    let [offsetX] = xy;
    // correct offsexX based on seekbar left position
    offsetX -= seekbarState.left;

    let time = FnSeekbar.pixelToSecond(offsetX, seekbarState.max, seekbarState.width);

    // limit to max range
    time = Math.min(seekbarState.max, time);

    setCutMarkerState((p) => ({
      ...p,
      offsetX: FnSeekbar.pixelToPercentage(offsetX, seekbarState.width),
      tooltip: new Date(Math.floor(time) * 1000).toISOString().substr(11, 8),
      time,
    }));
  });

  const getPercentageByTime = (time) => FnSeekbar.timeToPercentage(time, cutMode ? seekbarState.max : seekbarState.fakeMax, seekbarState.width);

  const handleSocketGetCurrentTime = (/* payload */) => {
    // payload ex: 1920x1080x157428438360
    // const split = payload.split("x");
    // const x = +split[0];
    // const y = +split[1];
    // const time = +split[2];
  };

  const handleSocketRewindChanged = (payload) => {
    const min = 0;
    const max = cutMode ? seekbarState.max : seekbarState.fakeMax;
    // const ms = +payload.ms;
    const fakeMs = +payload.fakeMs;
    // const second = ms / 1000;
    const fakeSecond = fakeMs / 1000;
    const formattedtime = FnSeekbar.formatTime(fakeSecond * 1000);
    const newVal = Number(((fakeSecond - min) * 100) / (max - min));

    setSeekbarState((prevState) => ({
      ...prevState,
      time: formattedtime,
      offset: `calc(${newVal}% + (${8 - newVal * 0.15}px))`,
      value: fakeSecond,
    }));
  };

  const between = (x, min, max) => x >= min && x <= max;

  const sum = (arr) => arr.reduce((a, b) => a + b, 0);

  const getFakeValue = (realTime) => {
    let clone = [...cuts];

    clone = clone.sort(([a, b], [c, d]) => a - c || d - b);

    const i = clone.findIndex((x) => between(realTime, x[0], x[1]));

    if (i === 0) return realTime;

    let cd = 0;
    for (let j = i - 1; j >= 0; j--) {
      const [s, e] = clone[j];
      cd += e - s;
    }

    return realTime - cd;
  };

  // const getRealValue = (fakeTime) => {
  //   let clone = [...cuts];
  //
  //   clone = clone.sort(([a, b], [c, d]) => a - c || d - b);
  //
  //   const i = clone.findIndex((x) => between(fakeTime, x[0], x[1]));
  //
  //   let cd = 0;
  //   for (let j = i; j >= 0; j--) {
  //     const [s, e] = clone[j];
  //     cd += e - s;
  //   }
  //
  //   return fakeTime + cd;
  // };

  const updateTimeRangeCut = (screens, cutRanges) => {
    const cutDurations = [];
    let prevScreenEnd = 0;

    for (let i = 0; i < screens.length; i++) {
      const screen = screens[i];
      const [s, e] = screen.timeRange;
      let cd = 0;
      for (let j = 0; j < cutRanges.length; j++) {
        const [cf, ct] = cutRanges[j];

        if (between(cf, s, e)) cd += ct - cf;
      }

      cutDurations.push(cd);

      const start = prevScreenEnd + (i === 0 ? 0 : 1);
      const end = e - sum(cutDurations);

      screen.timeRangeCut = [start, end];

      prevScreenEnd = end;
    }

    return [screens, cutRanges, sum(cutDurations)];
  };

  const handleSeekbarChange = () => {
    // const fakeSecond = +e.target.value;
    // const second = getRealValue(fakeSecond);
    // FnSeekbar.handleChange(second * 1000, fakeSecond * 1000, socket);
  };

  const calculateScreenWidths = (isPageLoad) => {
    const widths = [];
    seekbarData.seekbar_screens_list.forEach((x) => {
      const [s, e] = isPageLoad ? (cutMode ? x.timeRange : x.timeRangeCut) : cutMode ? x.timeRangeCut : x.timeRange;
      const width = FnSeekbar.timeToPercentage(
        e - s,
        isPageLoad ? (cutMode ? seekbarState.max : seekbarState.fakeMax) : cutMode ? seekbarState.fakeMax : seekbarState.max,
        seekbarState.width,
      );

      widths.push(width);
    });

    setScreenWidths(widths);
  };

  useEffect(() => {
    const handleFullScreenChange = () => {
      if (document.fullscreenElement) {
        const { left, width } = seekbarRef.current.getBoundingClientRect();
        setSeekbarState((prevState) => ({ ...prevState, width, left }));
      }
    };

    const handleWindowResize = () => {
      if (seekbarRef.current) {
        const { left, width } = seekbarRef.current.getBoundingClientRect();
        setSeekbarState((prevState) => ({ ...prevState, width, left }));
      }
    };

    document.addEventListener("fullscreenchange", handleFullScreenChange, false);

    window.addEventListener("resize", handleWindowResize, false);

    // FnSeekbar.socketSubToGetRewind(socket, handleSocketRewindChanged);
    // FnSeekbar.socketSubToGetCurrentTime(socket, handleSocketGetCurrentTime);

    // FnSeekbar.getScreens().then((res) => {
    //   const [seekbar_screens_list, skip_ranges, cutDuration] = updateTimeRangeCut(
    //     res.seekbar_screens_list,
    //     res.skip_ranges,
    //   );
    //
    //   setSeekbarData({ seekbar_screens_list, skip_ranges });
    //   setCuts(skip_ranges);
    //   setSeekbarState((prevState) => ({
    //     ...prevState,
    //     fakeMax: prevState.max - cutDuration,
    //   }));
    // });

    return () => {
      document.removeEventListener("fullscreenchange", handleFullScreenChange, false);

      document.removeEventListener("resize", handleWindowResize, false);

      // FnSeekbar.socketUnSubFromGetRewind(socket, handleSocketRewindChanged);
      // FnSeekbar.socketUnSubFromGetCurrentTime(socket, handleSocketGetCurrentTime);
    };
  }, []);

  useDidMountEffect(() => {
    let flag = false;
    for (let i = 0; i < cuts.length; i++) {
      const cutArray = cuts[i];

      if (cutArray[0] - CUT_RANGE_SAFE_MARGIN < cutMarkerState.time && cutMarkerState.time < cutArray[1] + CUT_RANGE_SAFE_MARGIN) {
        flag = true;
        break;
      }
    }

    setIsCursorInsideAnotherArea(flag);
  }, [cutMarkerState.time]);

  useDidMountEffect(() => {
    if (isDraggingRef.current) return;

    // FnSeekbar.updateSkipRange(cuts);

    const [seekbar_screens_list, skip_ranges, cutDuration] = updateTimeRangeCut(seekbarData.seekbar_screens_list, cuts);

    setSeekbarData({ seekbar_screens_list, skip_ranges });
    setSeekbarState((prevState) => ({
      ...prevState,
      fakeMax: prevState.max - cutDuration,
    }));
  }, [cuts]);

  useDidMountEffect(() => {
    calculateScreenWidths(true);
  }, [seekbarState.width]);

  const handleCutModeToggleClick = () => {
    setCutMode((p) => !p);

    calculateScreenWidths();
  };

  const handleMarkerMouseDown = () => {
    if (!cutMode) return;

    setCut([cutMarkerState.time, 0]);
    setCutMarkerState((p) => ({ ...p, mode: "end" }));
  };

  const handleMarkerMouseUp = () => {
    if (!cutMode) return;

    setCutMarkerState((p) => ({ ...p, mode: "start" }));

    const startAt = cut[0];
    let endAt = cutMarkerState.time;

    if (endAt - startAt < DELETE_OFFSET) {
      // reset temp cut
      setCut([0, 0]);
      return;
    }

    // prevent cross screen range
    for (let i = 0; i < seekbarData.seekbar_screens_list.length; i++) {
      const screen = seekbarData.seekbar_screens_list[i];

      if (startAt > screen.timeRange[0] && startAt < screen.timeRange[1]) {
        endAt = Math.min(endAt, screen.timeRange[1]);
        break;
      }
    }

    // prevent cross other ranges
    if (cuts.length > 0) {
      let sortedClone = [...cuts];
      sortedClone = sortedClone.sort(([a, b], [c, d]) => a - c || d - b);
      if (startAt < sortedClone[0][0]) {
        endAt = Math.min(endAt, sortedClone[0][0] - 2);
      } else {
        for (let i = 1; i < sortedClone.length; i++) {
          if (startAt > sortedClone[i - 1][1] && startAt < sortedClone[i][0]) endAt = Math.min(endAt, sortedClone[i][0] - 2);
        }
      }
    }

    // update cuts
    const clone = [...cuts];
    const newCut = [startAt, endAt];
    clone.push(newCut);
    setCuts(clone);

    // reset temp cut
    setCut([0, 0]);

    // FnSeekbar.submitSkipRange(newCut);
  };

  const hideControls = false;

  return (
    <footer className={`footer ${hideControls ? "hide" : ""}`}>
      <div className="seekbar-wrapper seekbar-wrapper--fullscreen" id="show-in-full-screen-mode">
        <div className={`seekbar-container ${cutMode ? "cut-video" : null} `}>
          {/* seekbar-btn */}
          <span className="flex">
            <img src={iconPlaySeekbar} alt="play" className="btn-36 btn-seekbar-play" />
          </span>
          <span className="flex expand-seekbar">
            <img src={iconSeekbarExpand} alt="seekbar-collapse" className="btn-16 btn-expand-seekbar m-lr-5" />
          </span>
          {/* seekbar */}
          <div {...bindMove()} ref={seekbarRef} className="seekbar-slider" onMouseDown={handleMarkerMouseDown} onMouseUp={handleMarkerMouseUp}>
            {/* screen sections */}
            {seekbarData.seekbar_screens_list.map((x, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <Fragment key={i}>
                <div
                  className="screen-item"
                  style={{
                    width: screenWidths[i],
                    backgroundColor: x.color,
                  }}>
                  {/* eslint-disable-next-line no-shadow */}
                  {x.comments.map((time, i) => (
                    <span
                      /* eslint-disable-next-line react/no-array-index-key */
                      key={i}
                      style={{
                        width: FnSeekbar.timeToPercentage(time, cutMode ? seekbarState.max : seekbarState.fakeMax, seekbarState.width),
                      }}
                    />
                  ))}
                </div>
                {i < seekbarData.seekbar_screens_list.length - 1 && (
                  <div className="transition-item">
                    <img src={transitionIcon} alt="transition" />
                  </div>
                )}
              </Fragment>
            ))}

            {/* delete tooltip */}
            {deleteTooltipState.visible && (
              <div
                style={{
                  left: deleteTooltipState.offsetX - 24,
                  position: "absolute",
                  top: -38,
                }}
                className="tooltip-type2 in-cut-mode delete-mode">
                Delete
              </div>
            )}

            {/* marker */}
            {cutMode && !isCursorInsideAnotherArea && (
              <div className={`fn__cutter-marker fn__marker cuter-${cutMarkerState.mode}`} style={{ left: cutMarkerState.offsetX }} />
            )}

            {/* marker (temp) */}
            {cutMode && cut[0] > 0 && (
              <>
                <div
                  data-time={cut[0]}
                  className="fn__marker fn__marker--temp"
                  style={{
                    left: getPercentageByTime(cut[0]),
                  }}
                  {...bindDrag()}
                />
                <div
                  style={{
                    left: getPercentageByTime(cut[0]),
                    width: `calc(${cutMarkerState.offsetX} - ${getPercentageByTime(cut[0])})`,
                  }}>
                  <div className="tooltip-type2 in-cut-mode" style={{ left: getPercentageByTime(cut[0]) }}>
                    <img src={iconCuter} width="10" className="m-lr-5" alt="" />
                    {FnSeekbar.formatTime(cut[0] * 1000)}
                  </div>
                </div>
              </>
            )}

            {/* markers (start & end) */}
            {cuts.map((c, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <div key={i} className={isDraggingRef.current ? "" : "animated"}>
                <div
                  data-time={c[0]}
                  className="fn__cutter fn__marker fn__cutter-marker--expanded fn__marker--start"
                  style={{
                    left: getPercentageByTime(cutMode ? c[0] : getFakeValue(c[0])),
                  }}
                  {...bindDrag()}
                />
                <div
                  data-time={c[1]}
                  className="fn__cutter fn__marker fn__cutter-marker--expanded fn__marker--end"
                  style={{
                    left: getPercentageByTime(cutMode ? c[1] : getFakeValue(c[0])),
                  }}
                  {...bindDrag()}
                />
                <div
                  className="fn__between-markers"
                  style={{
                    left: getPercentageByTime(cutMode ? c[0] : getFakeValue(c[0])),
                    width: cutMode ? `calc(${getPercentageByTime(c[1])} - ${getPercentageByTime(c[0])})` : 0,
                  }}
                />
                {!deleteTooltipState.visible && (
                  <div
                    className="tooltip-type2 in-cut-mode"
                    style={{
                      left: FnSeekbar.secondToPixel(c[0] + (c[1] - c[0]) / 2, seekbarState.max, seekbarState.width) - 55,
                      top: -37,
                      visibility: cutMode ? "visible" : "hidden",
                    }}>
                    <img src={iconCuter} width="10" className="m-lr-5" alt="" />
                    {FnSeekbar.formatTime(c[0] * 1000)} - {FnSeekbar.formatTime(c[1] * 1000)}
                  </div>
                )}
              </div>
            ))}

            {/* range input & time tooltip */}
            {!cutMode && (
              <>
                <input
                  type="range"
                  min="0"
                  max={cutMode ? seekbarState.max : seekbarState.fakeMax}
                  id="slider-range"
                  className="seekbar-input"
                  value={seekbarState.value}
                  onChange={handleSeekbarChange}
                />
                <span id="slider-value-in-player" style={{ left: seekbarState.offset }}>
                  {seekbarState.time}
                </span>
              </>
            )}
          </div>

          {/* seekbar-btn */}
          <span className="flex relative" id="show-volume-controller">
            <img
              src={iconVolume}
              alt="volume"
              className={`btn-22 btn-volume relative z-index-10 ${showVolumeController ? "active" : null} `}
              onClick={() => setShowVolumeController(!showVolumeController)}
            />
            <VolumeController show={showVolumeController} onChange={(e) => setVolume(e.target.value)} value={volume} />
          </span>
          <span className="flex p-lr-5 relative" id="go-to-cut-mode" onClick={handleCutModeToggleClick}>
            <img src={iconCuter} alt="cuter" className={`btn-22 btn-cuter ${cutMode ? "active" : null} `} />
            <div className="tooltip-type2"> Trim </div>
          </span>
          <span className="flex">
            <img src={iconLiveSeekbar} alt="live" className="btn-36 btn-seekbar-live" />
          </span>
        </div>
      </div>
    </footer>
  );
};

export default PlayerControllers;
